import React, { useState } from 'react';
import { Home, Mail, Users, BarChart2, Plus, ChevronUp } from 'lucide-react';
import { ViewType } from '../../types';

interface MobileBottomNavProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  onCreatePost: () => void;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ 
  currentView, 
  onViewChange, 
  onCreatePost 
}) => {
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  
  const navItems = [
    { id: 'dashboard' as ViewType, label: 'Home', icon: Home, color: 'blue' },
    { id: 'posts' as ViewType, label: 'Posts', icon: Mail, color: 'green' },
    { id: 'subscribers' as ViewType, label: 'Audience', icon: Users, color: 'purple' },
    { id: 'analytics' as ViewType, label: 'Analytics', icon: BarChart2, color: 'orange' },
  ];

  const createOptions = [
    { 
      label: 'New Post', 
      action: onCreatePost, 
      icon: Mail,
      description: 'Write a new article or newsletter'
    },
    { 
      label: 'Import Audience', 
      action: () => onViewChange('subscribers'), 
      icon: Users,
      description: 'Add subscribers from CSV'
    },
    { 
      label: 'View Analytics', 
      action: () => onViewChange('analytics'), 
      icon: BarChart2,
      description: 'Check your performance'
    },
  ];

  const handleCreateClick = () => {
    if (showCreateMenu) {
      onCreatePost();
    } else {
      setShowCreateMenu(true);
      // Auto-close after 5 seconds
      setTimeout(() => setShowCreateMenu(false), 5000);
    }
  };

  return (
    <>
      {/* Create Menu Overlay */}
      {showCreateMenu && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black bg-opacity-30"
            onClick={() => setShowCreateMenu(false)}
          />
          <div className="absolute bottom-20 left-4 right-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Quick Actions</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">What would you like to create?</p>
            </div>
            <div className="space-y-3">
              {createOptions.map(({ label, action, icon: Icon, description }, index) => (
                <button
                  key={label}
                  onClick={() => {
                    action();
                    setShowCreateMenu(false);
                  }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 transform hover:scale-[1.02] touch-manipulation min-h-[60px]"
                  data-testid="touch-friendly"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-xl">
                    <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{label}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40 shadow-lg safe-area-pb" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around py-2 px-2">
          {navItems.map(({ id, label, icon: Icon, color }) => (
            <button
              key={id}
              onClick={() => onViewChange(id)}
              className={`flex flex-col items-center py-2 px-3 rounded-xl transition-all duration-200 transform active:scale-95 touch-manipulation min-h-[60px] ${
                currentView === id || (id === 'posts' && currentView === 'editor')
                  ? `text-${color}-600 dark:text-${color}-400 bg-${color}-50 dark:bg-${color}-900/20`
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              data-testid="touch-friendly"
            >
              <div className={`p-2 rounded-lg transition-colors ${
                currentView === id || (id === 'posts' && currentView === 'editor')
                  ? `bg-${color}-100 dark:bg-${color}-900/30`
                  : 'bg-transparent'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium mt-1 text-gray-900 dark:text-gray-100">{label}</span>
              {(currentView === id || (id === 'posts' && currentView === 'editor')) && (
                <div className={`w-1 h-1 rounded-full bg-${color}-600 mt-1`} />
              )}
            </button>
          ))}
          
          {/* Create Button - Prominent FAB Style */}
          <div className="relative">
            <button
              onClick={handleCreateClick}
              className={`w-14 h-14 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 transform active:scale-95 touch-manipulation ${
                showCreateMenu ? 'rotate-45 scale-110' : 'hover:scale-105'
              }`}
              data-testid="touch-friendly"
            >
              <Plus className={`w-6 h-6 transition-transform duration-200 ${
                showCreateMenu ? 'rotate-45' : 'rotate-0'
              }`} />
            </button>
            
            {/* Create Menu Indicator */}
            {showCreateMenu && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                <ChevronUp className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;